import React, { useEffect, useMemo, useState } from "react";
import SelectTemplateOption from "../../../../../leads/lead-view/lead-view-details/lead-view-details-view/lead-view-details-view-email-info/select-template-option-dropdown";
import { emailComposeOptions } from "../../../../../leads/lead-filter/lead-filter.constant";
import { useLogin } from "../../../../../../common/hooks/useUser";
import "./campaign-sms-info.scss";
import DataTable from "../campaign-view-details-view-activities-info/task-table/task-table";
import TemplateListPopup from "../../../../../../common/components/templates-list-popup/template-list-popup";
import {
  AlertComponent,
  ConfirmationPopup,
} from "../../../../../../common/components";
import { useSetLoading } from "../../../../../../common/hooks/appearance";
import {
  getEmailLetterTemplates,
  getTemplateHeaderOrFooter,
} from "../../../../../../api/email-letter-templates";
import {
  deleteCampaignSMSDetails,
  deleteEmail,
  getCampaignEmails,
  getCampaignSMSHistory,
  getEachCampaignEmailDetails,
  getEachCampaignSMSDetails,
} from "../../../../../../api/campaign";
import ComposeSMSDialog from "./compose-sms";
import { formatDateTime } from "../../../../../../utils/utils";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import DeleteIcon from "../../../../../../assets/icons/archive-icon.svg";
import EmailSetUpBanner from "../../../../../../common/components/email-setup-required-banner/email-setup-banner";
import SMSSetUpBanner from "../../../../../../common/components/sms-setup-required-banner/sms-setup-banner";
import MODULES from "../../../../../../constants/modules";
interface CampaignSMSInfoProps {
  campaignDetails?: any;
  setLoading: (loading: boolean) => void;
  setAlertMessage: (message: string) => void;
  setAlertType: (type: "success" | "error") => void;
  setAlertOpen: (open: boolean) => void;
  permissions?: any;
  fetchCampaignDetails?: () => Promise<void> | void;
}

const CampaignSMSInfo: React.FC<CampaignSMSInfoProps> = ({
  campaignDetails,
  setLoading,
  setAlertMessage,
  setAlertType,
  setAlertOpen,
  permissions,
  fetchCampaignDetails,
}) => {
  const [selectedOption, setSelectedOption] = useState("");
  const [smsData, setSmsData] = useState([]);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isRecipientsOpen, setIsRecipientsOpen] = useState(false);
  const [recipientData, setRecipientData] = useState<any[]>([]);
  const [smsToDelete, setSMSToDelete] = useState<any>(null);
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);

  const loginDetails = useLogin();
  const isTwilioConfigured = loginDetails?.isTwilioConfigured;

  const campaignSMSColumns = [
    {
      label: "Message",
      key: "message",
      render: (row: any) => row.message_text,
    },
    {
      label: "Owner",
      key: "owner",
      render: (row: any) =>
        [row?.createdBy?.first_name, row?.createdBy?.last_name]
          .filter(Boolean)
          .join(" "),
    },
    {
      label: "Recipients",
      key: "recipients",
      render: (row: any) => (
        <span
          style={{
            cursor: row.total_recipients > 0 ? "pointer" : "default",
            color: "#1194DA",
            textDecoration: row.total_recipients === 0 ? "none" : "underline",
          }}
          onClick={() => {
            if (row.total_recipients === 0) return;
            setIsRecipientsOpen(true);
            fetchRecipientDetails(row.id);
          }}
        >
          {row.total_recipients}
        </span>
      ),
    },
    {
      label: "Date & time",
      key: "dateTime",
      render: (row: any) => formatDateTime(row.created_at),
    },
    {
      label: "Status",
      key: "status",
      render: (row: any) => (row.status ? "Sent" : "Failed"),
    },
  ];

  const recipentColumns = [
    { label: "Name", key: "name", render: (row: any) => row.name },
    { label: "Phone Number", key: "phone", render: (row: any) => row.phone },
    {
      label: "Delivery Status",
      key: "deliveryStatus",
      render: (row: any) => row.deliveryStatus,
    },
    { label: "Time", key: "time", render: (row: any) => row.time },
  ];

  const handleOptionChange = async (e: any) => {
    setSelectedOption(e.target.value);
    // Refresh campaign details to ensure recipients are up-to-date
    if (fetchCampaignDetails) {
      try {
        setLoading(true);
        await fetchCampaignDetails();
      } finally {
        setLoading(false);
      }
    }
    if (e.target.value === "Compose New Mail") {
      setIsComposeOpen(true);
    }
    if (e.target.value === "Select from Templates") {
      // Open the template list popup
      setIsComposeOpen(false);
    }
  };

  const fetchRecipientDetails = async (campaignSMSId: number) => {
    setLoading(true);
    try {
      const response = await getEachCampaignSMSDetails(campaignDetails?.id, campaignSMSId);
      const formattedResponse = response?.data?.data?.map((item: any) => {
        return {
          name:
            [
              item?.lead?.first_name,
              item?.lead?.middle_name,
              item?.lead?.last_name,
            ]
              .filter(Boolean)
              .join(" ") ||
            [
              item?.customer?.first_name,
              item?.customer?.middle_name,
              item?.customer?.last_name,
            ]
              .filter(Boolean)
              .join(" ") ||
            "-",
          phone:
            item?.phone_number,
          deliveryStatus: item.sms_status,
          time: formatDateTime(item.created_at),
        };
      });
      setRecipientData(formattedResponse);
    } catch (error) {
      console.error("Error fetching recipient details:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchSMSHistory = async () => {
    setLoading(true);
    try {
      const response = await getCampaignSMSHistory(campaignDetails?.id);
      setSmsData(response.data.data);
    } catch (error) {
      console.error("Error fetching SMS history:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchSMSHistory();
  }, []);

  const handleSMSSuccess = () => {
    setAlertMessage("SMS Sent Succeessfully.");
    setAlertType("success");
    setAlertOpen(true);
    setIsComposeOpen(false);
    fetchSMSHistory();
    setSelectedOption("");
  };

  const handleDeleteSMS = (sms: any) => {
    setSMSToDelete(sms);
    setIsArchivePopupOpen(true);
  };

  const confirmDeleteSMS = async () => {
    if (!smsToDelete) return;
    setLoading(true);
    try {
      await deleteCampaignSMSDetails(campaignDetails?.id, smsToDelete.id);
      setAlertMessage("SMS archived successfully.");
      setAlertType("success");
      setAlertOpen(true);
      fetchSMSHistory();
    } catch (error: any) {
      setAlertMessage("Failed to archive SMS.");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsArchivePopupOpen(false);
      setSMSToDelete(null);
      setLoading(false);
    }
  };
  const handleAddNewClick = () => {
    setIsComposeOpen(true);
  };

  return (
    <div className="campaign-sms-info">
      <div className="campaign-sms-info-header">
        <div className="campaign-sms-info-header-text">SMS</div>
        <button
          className="campaign-view-details-view-sms-info-header-button"
          onClick={handleAddNewClick}
          disabled={!permissions?.canWrite} //|| !isTwilioConfigured}
        >
          Compose SMS
        </button>
      </div>
      {!isTwilioConfigured && (
        <div className="sms-setup-banner-wrapper">
          <SMSSetUpBanner />
        </div>
      )}
      {smsData && smsData.length > 0 ? ( console.log("smsData", smsData),
        <div className="campaign-email-table">
          <DataTable
            columns={campaignSMSColumns}
            data={smsData}
            rowActions={(sms: any) => (
              <div style={{ display: "flex" }}>
                <Tooltip className="tooltip" title="Archive">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSMS(sms);
                    }}
                    className="icon-button"
                    disabled={!permissions?.canDelete}
                  >
                    <img src={DeleteIcon} alt="archive" className="img-icon" />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          />
        </div>
      ) : (
        <div className="campaign-sms-info-no-data">No records found.</div>
      )}

      <ConfirmationPopup
        open={isArchivePopupOpen}
        onCancel={() => {
          setIsArchivePopupOpen(false);
          setSMSToDelete(null);
        }}
        onOkay={confirmDeleteSMS}
        title={`Archive  SMS`}
        note={
          smsToDelete
            ? "Are you sure you want to archive the SMS?"
            : "Are you sure you want to archive this SMS?"
        }
        buttonText="Archive"
      />

      <ComposeSMSDialog
        open={isComposeOpen}
        onClose={() => {
          setIsComposeOpen(false);
          setSelectedOption("");
        }}
        // leadDetails={leadDetails}
        campaignDetails={campaignDetails}
        onSuccess={handleSMSSuccess}
      />
      <Dialog open={isRecipientsOpen} maxWidth="md" fullWidth>
        <DialogTitle>
          Recipients
          <IconButton
            aria-label="close"
            onClick={() => setIsRecipientsOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {recipientData.length > 0 ? (
            <DataTable columns={recipentColumns} data={recipientData} />
          ) : (
            <div className="campaign-sms-info-no-data">No records found.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignSMSInfo;
